import { useState, useEffect, useMemo, useCallback } from 'react';
import { filterDeskSpaces, generateMarkersFromSpaces } from '../utils/markerGenerator';

/**
 * Custom hook for managing floor plan markers
 * @param {Object} signageState - Signage state
 * @returns {Object} Markers data and update functions
 */
export const useMarkers = (signageState) => {
  const [markersUpdateTrigger, setMarkersUpdateTrigger] = useState(0);

  // Combined effect for markers updates - triggers when spaces or their states change
  useEffect(() => {
    if (signageState?.spaces && signageState.spaces.length > 0) {
      console.log('🔄 Updating floor plan markers based on signageState changes...');
      setMarkersUpdateTrigger(prev => prev + 1);
    }
  }, [signageState?.spaces, signageState?.spaces?.map(space => space.senses?.availability?.state).join(',')]);

  // Cached filtered spaces to avoid repeated filtering
  const filteredSpaces = useMemo(() => {
    return filterDeskSpaces(signageState?.spaces);
  }, [signageState?.spaces]);

  // Create markers data from signage spaces (optimized)
  const markers = useMemo(() => {
    if (!filteredSpaces.length) return [];
    
    const updatedMarkers = generateMarkersFromSpaces(filteredSpaces);
    
    // Reduced logging - only log when markers actually update
    if (markersUpdateTrigger > 0 && updatedMarkers.length > 0) {
      console.log(`📍 Updated ${updatedMarkers.length} floor plan markers reactively`);
    }
    
    return updatedMarkers;
  }, [filteredSpaces, markersUpdateTrigger]);

  const forceMarkersUpdate = useCallback(() => {
    setMarkersUpdateTrigger(prev => prev + 1);
  }, []);

  return {
    markers,
    markersUpdateTrigger,
    forceMarkersUpdate
  };
};
